import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, authFileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, PackageCheck, Truck, Camera, Upload, KeyRound, Star, MapPin, Flag, Share2, Navigation } from "lucide-react";
import TrackingMap from "@/components/TrackingMap";
import Chat from "@/components/Chat";
import RazorpayPay from "@/components/RazorpayPay";
import PaymentStep from "@/components/PaymentStep";
import ContactButtons from "@/components/ContactButtons";
import CarrierSetupDialog, { isSetupError } from "@/components/CarrierSetupDialog";
import { sizeLabel } from "@/lib/sizes";
import RatingPrompt from "@/components/RatingPrompt";
import ReportIssueButton from "@/components/ReportIssueButton";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";

const steps = ["requested", "awaiting_payment", "accepted", "picked_up", "in_transit", "delivered"];
const stepLabels = {
  requested: "Requested",
  awaiting_payment: "Awaiting payment",
  accepted: "Accepted",
  picked_up: "Picked up",
  in_transit: "In transit",
  delivered: "Delivered",
};

const BRow = ({ label, value, testid }) => (
  <div className="flex justify-between" data-testid={testid}><span>{label}</span><span>₹{Number(value || 0).toFixed(0)}</span></div>
);

const PayoutBreakdown = ({ b, payout }) => (
  <>
    <BRow label="Handover fee" value={b.handover_fee}/>
    {b.route_component !== undefined && <BRow testid="payout-route" label={`Route (${b.route_km || 0} km)`} value={b.route_component}/>}
    {b.route_component !== undefined && <BRow testid="payout-detour" label={`Detour (${b.detour_km || 0} km)`} value={b.detour_component}/>}
    {b.route_component === undefined && <BRow label="Distance" value={b.distance_component}/>}
    <BRow label="Size fee" value={b.size_component}/>
    {b.min_floor_applied && <BRow label="Top-up to minimum earnings" value={(b.min_floor || 0) - (b.pre_floor_earnings || 0)}/>}
    {b.door_addons_total > 0 && <BRow label="Door add-ons" value={b.door_addons_total}/>}
    <div className="flex justify-between border-t border-slate-700 pt-1 mt-1 font-semibold"><span>Net to you</span><span>₹{Number(payout || 0).toFixed(0)}</span></div>
    <div className="text-[10px] text-slate-400">Platform fee is added to the sender's price, not taken from you.</div>
  </>
);

const DeliveryDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [dropPin, setDropPin] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [mismatchNote, setMismatchNote] = useState("");
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDetails, setDisputeDetails] = useState("");
  const [disputes, setDisputes] = useState([]);

  const load = async () => {
    const r = await api.get(`/deliveries/${id}`);
    setD(r.data);
    setProgress(r.data.progress_percent || 0);
    try { const dp = await api.get(`/deliveries/${id}/disputes`); setDisputes(dp.data); } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [id]);

  if (!d) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  const isSender = d.sender_id === user?.id;
  const isCarrier = d.carrier_id === user?.id;
  const currentIdx = steps.indexOf(d.status);

  const accept = async () => { setBusy(true); try { await api.post(`/deliveries/${id}/accept`); await load(); toast.success("Accepted — waiting for the sender to pay"); } catch(e){ if (isSetupError(e)) setSetupOpen(true); else toast.error(e?.response?.data?.detail);} setBusy(false); };

  const declineMismatch = async () => {
    if (!mismatchNote.trim()) { toast.error("Please note what didn't match"); return; }
    setBusy(true);
    try {
      await api.post(`/deliveries/${id}/decline-size-mismatch`, { note: mismatchNote.trim() });
      toast.success("Delivery declined · sender refunded");
      setMismatchOpen(false);
      setMismatchNote("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const doPickup = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/${id}/pickup`, { pin, photo_id: photoId });
      toast.success("Picked up · en route");
      setPickupOpen(false); setPin(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid PIN"); }
    setBusy(false);
  };
  const doDeliver = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/${id}/deliver`, { delivery_id: id, pin });
      toast.success("Delivered · payment released");
      setDeliverOpen(false); setPin(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid PIN"); }
    setBusy(false);
  };
  const getDropPin = async () => {
    try { const r = await api.get(`/deliveries/${id}/drop-pin`); setDropPin(r.data.drop_pin); } catch (e) { toast.error(e?.response?.data?.detail || "Not yet available"); }
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: {"Content-Type": "multipart/form-data"}}); setPhotoId(r.data.file_id); toast.success("Photo uploaded"); } catch { toast.error("Upload failed"); }
  };
  const rate = async () => {
    try { await api.post("/ratings", { delivery_id: id, stars }); toast.success("Thanks for rating"); setRateOpen(false); load(); } catch(e){ toast.error(e?.response?.data?.detail); }
  };
  const saveProgress = async (pct) => {
    try {
      await api.post(`/deliveries/${id}/progress`, { progress_percent: pct });
      setProgress(pct);
      toast.success(`Progress · ${pct}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const raiseDispute = async () => {
    if (!disputeReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/deliveries/${id}/disputes`, { reason: disputeReason, details: disputeDetails });
      toast.success("Dispute raised · carrier payout on hold · admin will review");
      setDisputeOpen(false); setDisputeReason(""); setDisputeDetails("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copyTrackLink = () => {
    const url = `${window.location.origin}/track/${d.track_token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Tracking link copied · share with the receiver");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/app" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4"/> Back</Link>

        <div className="rounded-3xl bg-white border p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Delivery</div>
              <h1 className="font-display text-2xl font-extrabold">{d.parcel?.pickup_location} → {d.parcel?.drop_location}</h1>
              <div className="text-sm text-slate-500 mt-1"><span data-testid="delivery-parcel-size">{sizeLabel(d.parcel)}</span>{d.parcel?.description ? ` · ${d.parcel.description}` : ""}</div>
              {d.eta?.available && d.status !== "delivered" && (
                <div className="mt-2 inline-flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-1" data-testid="eta-badge">
                    <MapPin className="w-3 h-3 text-amber-600"/>
                    {d.status === "in_transit" && d.eta.arrival_by
                      ? `Arrival by ${new Date(d.eta.arrival_by).toLocaleString("en-IN", {month:"short", day:"numeric", hour:"numeric", minute:"2-digit"})}`
                      : `Estimated · ${d.eta.display}`}
                  </span>
                  {d.eta.same_day && <Badge className="bg-emerald-100 text-emerald-700">Same-day</Badge>}
                </div>
              )}
            </div>
            <Badge className="bg-amber-100 text-amber-800 uppercase">{d.status.replace("_"," ")}</Badge>
          </div>

          <CarrierSetupDialog tripId={d.trip_id} open={setupOpen} onClose={() => setSetupOpen(false)}/>
          {isSender && d.carrier?.photo_file_id && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 p-3" data-testid="carrier-photo-card">
              <img src={authFileUrl(d.carrier.photo_file_id)} alt={`${d.carrier.name}'s photo`} className="w-14 h-14 rounded-xl object-cover" data-testid="carrier-photo"/>
              <div className="text-sm"><div className="font-semibold" data-testid="carrier-display-name">{d.carrier.name}</div><div className="text-xs text-slate-500">Your traveller — check it's them at pickup.</div></div>
            </div>
          )}
          {/* Price display */}
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {isSender && (
              <div className="rounded-2xl bg-slate-900 text-white p-5">
                <div className="text-xs uppercase text-slate-400 tracking-wider">{d.status === "awaiting_payment" ? "Amount to pay" : d.payment_method === "cod" ? "Pay cash at drop-off" : "You paid online"}</div>
                <div className="mt-1 font-display text-4xl font-extrabold">₹{d.sender_total.toFixed(0)}</div>
                <div className="text-xs text-slate-400 mt-1">All inclusive · released on delivery PIN</div>
              </div>
            )}
            {isCarrier && (
              <div className="rounded-2xl bg-slate-900 text-white p-5">
                <div className="text-xs uppercase text-amber-300 tracking-wider">Your payout</div>
                <div className="mt-1 font-display text-4xl font-extrabold">₹{d.carrier_payout.toFixed(0)}</div>
                <div className="text-xs text-slate-300 mt-2 space-y-0.5">
                  <PayoutBreakdown b={d.price_breakdown || {}} payout={d.carrier_payout}/>
                </div>
              </div>
            )}

            {isSender && d.status === "accepted" && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5">
                <div className="text-xs uppercase text-amber-800 tracking-wider flex items-center gap-1"><KeyRound className="w-3 h-3"/> Pickup PIN</div>
                <div className="font-mono text-4xl font-extrabold mt-1 tracking-widest" data-testid="pickup-pin">{d.pickup_pin}</div>
                <div className="text-xs text-amber-800/80 mt-1">Share with your carrier at pickup only.</div>
              </div>
            )}
            {isSender && (d.status === "in_transit" || d.status === "delivered") && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
                <div className="text-xs uppercase text-emerald-800 tracking-wider flex items-center gap-1"><KeyRound className="w-3 h-3"/> Drop PIN</div>
                {dropPin ? (
                  <div className="font-mono text-4xl font-extrabold mt-1 tracking-widest" data-testid="drop-pin">{dropPin}</div>
                ) : (
                  <Button data-testid="reveal-drop-pin" onClick={getDropPin} size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700">Reveal to share with receiver</Button>
                )}
                <div className="text-xs text-emerald-800/80 mt-2">Only share with the receiver at drop-off.</div>
              </div>
            )}
          </div>

          {/* Addresses */}
          {(d.parcel?.pickup_point || d.parcel?.drop_point) && (
            <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="addresses-block">
              {["pickup_point", "drop_point"].map((k) => {
                const pt = d.parcel?.[k];
                if (!pt) return null;
                const isPickup = k === "pickup_point";
                const masked = pt.masked;
                return (
                  <div key={k} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3 text-amber-600"/> {isPickup ? "Pickup" : "Drop-off"}</div>
                    <div className="mt-1 font-semibold text-slate-900 truncate">{pt.area || pt.city}, {pt.city}</div>
                    {!masked ? (
                      <>
                        <div className="text-xs text-slate-600 mt-1">{pt.address}</div>
                        {pt.pincode && <div className="text-[11px] text-slate-500 mt-0.5">PIN {pt.pincode}</div>}
                        <a
                          data-testid={`navigate-${k}`}
                          href={`https://www.google.com/maps/dir/?api=1&destination=${pt.lat},${pt.lng}`}
                          target="_blank" rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                        ><Navigation className="w-3 h-3"/> Open in Maps</a>
                      </>
                    ) : (
                      <div className="text-xs text-slate-500 mt-1">Full address unlocks once the request is accepted.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Timeline */}
          <div className="mt-6">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Status</div>
            <div className="space-y-3">
              {steps.map((s, i) => (
                <div key={s} className="timeline-dot flex items-start gap-3">
                  {i <= currentIdx ? <CheckCircle2 className="w-6 h-6 text-emerald-500"/> : <Circle className="w-6 h-6 text-slate-300"/>}
                  <div>
                    <div className={`font-medium ${i <= currentIdx ? "text-slate-900" : "text-slate-400"}`}>{stepLabels[s]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment step — new unified Razorpay + COD surface for the sender */}
          {isSender && d.status === "awaiting_payment" && (
            <div className="mt-6">
              <PaymentStep delivery={d} user={user} onDone={load}/>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            {isCarrier && d.status === "requested" && <Button data-testid="accept-btn" onClick={accept} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><PackageCheck className="w-4 h-4 mr-1"/> Accept request</Button>}
            {isCarrier && d.status === "accepted" && <Button data-testid="pickup-btn" onClick={() => setPickupOpen(true)} className="rounded-full bg-amber-600 hover:bg-amber-700"><Camera className="w-4 h-4 mr-1"/> Confirm pickup</Button>}
            {isCarrier && d.status === "accepted" && <Button data-testid="mismatch-btn" onClick={() => setMismatchOpen(true)} variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"><Flag className="w-4 h-4 mr-1"/> Size/weight mismatch</Button>}
            {isCarrier && d.status === "in_transit" && <Button data-testid="deliver-btn" onClick={() => setDeliverOpen(true)} className="rounded-full bg-slate-900 hover:bg-slate-800"><Truck className="w-4 h-4 mr-1"/> Confirm delivery</Button>}
            {isSender && d.track_token && ["accepted","picked_up","in_transit"].includes(d.status) && (
              <Button data-testid="share-track-btn" onClick={copyTrackLink} variant="outline" className="rounded-full"><Share2 className="w-4 h-4 mr-1"/> Share tracking link</Button>
            )}
            {(isSender || isCarrier) && d.status !== "cancelled" && !d.payout_frozen && (
              <ReportIssueButton deliveryId={d.id} onDone={load}/>
            )}
          </div>

          {d.payout_frozen && (
            <div className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900">
              <div className="flex items-center gap-2 font-semibold"><Flag className="w-4 h-4"/> Payout on hold — dispute under review</div>
              {disputes.filter(d => d.status === "open").map(dp => (
                <div key={dp.id} className="mt-1 text-xs text-rose-800/80">
                  {dp.raised_role.toUpperCase()} · {dp.reason}
                </div>
              ))}
            </div>
          )}

          {d.pickup_photo_id && (
            <div className="mt-6">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pickup condition photo</div>
              <img src={authFileUrl(d.pickup_photo_id)} alt="pickup" className="rounded-xl w-full max-w-xs"/>
            </div>
          )}
        </div>

        {(d.status === "in_transit" || d.status === "delivered") && (
          <div className="mt-4 rounded-3xl bg-white border p-6">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-amber-600"/>
              <div className="font-display font-bold">Live tracking</div>
              <div className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">{d.status === "delivered" ? "Completed" : "Auto-refreshing"}</div>
            </div>
            <TrackingMap
              pickup={d.parcel?.pickup_location}
              drop={d.parcel?.drop_location}
              progressPercent={d.progress_percent || 0}
              lastLocation={d.last_location}
            />
            {isCarrier && d.status === "in_transit" && (
              <div className="mt-4">
                <label className="text-xs text-slate-500 uppercase tracking-wider">Update your progress</label>
                <div className="mt-3 px-1">
                  <Slider data-testid="progress-slider" min={0} max={99} step={1} value={[progress]} onValueChange={([v]) => setProgress(v)} onValueCommit={([v]) => saveProgress(v)}/>
                  <div className="text-sm mt-2 font-mono text-amber-700">{progress}%</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">Move the slider as you travel · sender sees the truck moving.</div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4"><ContactButtons delivery={d} role={isSender ? "sender" : "carrier"}/></div>
        {(d.status !== "requested" && d.status !== "awaiting_payment") && (
          <div className="mt-4" id="chat">
            <Chat deliveryId={id}/>
          </div>
        )}

        {d.status === "delivered" && (
          <RatingPrompt
            delivery={d}
            role={isSender ? "sender" : "carrier"}
            onDone={load}
          />
        )}
      </div>

      {/* Pickup dialog */}
      <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm pickup</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 hover:bg-slate-50">
              <Upload className="w-4 h-4"/> {photoId ? "Photo attached" : "Attach parcel photo"}
              <input data-testid="pickup-photo" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
            </label>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Enter pickup PIN from sender</label>
              <div className="mt-2 flex justify-center">
                <InputOTP maxLength={4} value={pin} onChange={setPin} data-testid="pickup-pin-input">
                  <InputOTPGroup>{[0,1,2,3].map(i => <InputOTPSlot key={i} index={i}/>)}</InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="confirm-pickup-btn" disabled={busy} onClick={doPickup} className="bg-amber-600 hover:bg-amber-700">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliver dialog */}
      <Dialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm delivery</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-500">Ask the receiver for their drop PIN.</div>
            <div className="flex justify-center">
              <InputOTP maxLength={4} value={pin} onChange={setPin} data-testid="drop-pin-input">
                <InputOTPGroup>{[0,1,2,3].map(i => <InputOTPSlot key={i} index={i}/>)}</InputOTPGroup>
              </InputOTP>
            </div>
          </div>
          <DialogFooter><Button data-testid="confirm-deliver-btn" disabled={busy} onClick={doDeliver} className="bg-slate-900 hover:bg-slate-800">Confirm delivery</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rate your carrier</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setStars(n)} className="p-1">
                <Star className={`w-8 h-8 ${n <= stars ? "fill-amber-400 stroke-amber-500" : "text-slate-300"}`}/>
              </button>
            ))}
          </div>
          <DialogFooter><Button data-testid="submit-rating-btn" onClick={rate} className="bg-amber-600 hover:bg-amber-700">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute dialog */}
      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report an issue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Raising a dispute puts the carrier's payout on hold until an admin reviews the case.</div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Reason</label>
              <Input data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} className="rounded-xl mt-1" placeholder="e.g., Parcel damaged, wrong item"/>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Details (optional)</label>
              <Textarea data-testid="dispute-details" value={disputeDetails} onChange={(e) => setDisputeDetails(e.target.value)} className="rounded-xl mt-1" placeholder="Share more context"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)}>Cancel</Button>
            <Button data-testid="submit-dispute-btn" onClick={raiseDispute} className="bg-rose-600 hover:bg-rose-700">Raise dispute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mismatchOpen} onOpenChange={setMismatchOpen}>
        <DialogContent data-testid="mismatch-dialog">
          <DialogHeader><DialogTitle>Decline — size or weight mismatch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">The parcel is visibly larger or heavier than what the sender declared. No penalty on you — the sender gets refunded and is warned against future mismatches.</div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">What didn't match?</label>
              <Textarea data-testid="mismatch-note" value={mismatchNote} onChange={(e) => setMismatchNote(e.target.value)} className="rounded-xl mt-1" placeholder="e.g. Declared as Small but weighs 6 kg, needs SUV boot"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMismatchOpen(false)}>Cancel</Button>
            <Button data-testid="submit-mismatch-btn" disabled={!mismatchNote.trim() || busy} onClick={declineMismatch} className="bg-rose-600 hover:bg-rose-700">Decline & refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryDetail;
